import { useEffect, useState } from 'react';
import algosysLogo from '../assets/algosys_logo.png';
import { BUNDLED_LOGO_ALT, LOGO_FALLBACK } from './brandingPresentation';

// ─── Brand Logo Image ─────────────────────────────────────────────────────────
// Shared brand mark for every branded surface (auth pages, sidebar).
//
// Renders the portal's custom logo only when a validated object URL is
// available. Otherwise `fallback` decides what fills the slot:
//   - `bundled`:     the AlgoSys platform logo (main AlgoSys host only);
//   - `placeholder`: a small neutral, decorative block that names no one;
//   - `none`:        nothing.
// A custom image that fails to load is not replaced by the AlgoSys logo, and a
// bundled asset that fails to load hides itself rather than leaving a broken
// placeholder. The default is `none` so a caller can never show the AlgoSys
// logo by omission.
export default function BrandLogoImage({
  logoSrc = null,
  alt = null,
  fallback = LOGO_FALLBACK.NONE,
  className,
}) {
  const [customFailed, setCustomFailed] = useState(false);
  // A new object URL is a different image, so a previous failure must not mask it.
  useEffect(() => { setCustomFailed(false); }, [logoSrc]);

  if (logoSrc && !customFailed) {
    return (
      <img
        src={logoSrc}
        alt={alt || ''}
        className={className}
        onError={() => setCustomFailed(true)}
      />
    );
  }

  if (fallback === LOGO_FALLBACK.BUNDLED) {
    return (
      <img
        src={algosysLogo}
        alt={BUNDLED_LOGO_ALT}
        className={className}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }

  if (fallback === LOGO_FALLBACK.PLACEHOLDER) {
    return (
      <span
        aria-hidden="true"
        data-brand-logo-placeholder=""
        className={`${className ?? ''} inline-flex items-center justify-center align-middle`}
      >
        <span className="block h-8 w-8 rounded-lg bg-txt-muted/15" />
      </span>
    );
  }

  return null;
}
